/**
 * Voice recording for Storybook answers — reuses story audio + transcription APIs.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { transcribeStoryAndWait, transcribeStorySegmentAndWait } from '../../utils/storyTranscription';
import { storybookApi } from '../../utils/storybook';
import { transcriptionStatusLabel } from '../../utils/storybookGuidance';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();
const MAX_RECORDING_SECONDS = 600;

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function useStorybookVoice({
  storybookId,
  promptId,
  draftTitle,
  storyId: initialStoryId,
  onStoryId,
  onTranscript,
}) {
  const [storyId, setStoryId] = useState(initialStoryId || null);
  const [storyData, setStoryData] = useState(null);
  const [recordingState, setRecordingState] = useState('inactive');
  const [recordingTime, setRecordingTime] = useState(0);
  const [continueRecording, setContinueRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionPhase, setTranscriptionPhase] = useState(null);
  const [micPermission, setMicPermission] = useState('unknown');

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const recordingTimeRef = useRef(0);
  const shouldUploadOnStopRef = useRef(false);
  const finalizeRecordingRef = useRef(null);

  useEffect(() => {
    setStoryId(initialStoryId || null);
  }, [initialStoryId]);

  const loadStory = useCallback(async (sid) => {
    if (!sid) return null;
    try {
      const { data } = await axios.get(`${API}/stories/${sid}`);
      setStoryData(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (storyId) loadStory(storyId);
  }, [storyId, loadStory]);

  const ensureStory = useCallback(async () => {
    if (storyId) return storyId;
    const result = await storybookApi.ensureStory(storybookId, {
      prompt_id: promptId,
      title: draftTitle?.trim() || null,
    });
    const sid = result.story_id;
    if (sid) {
      setStoryId(sid);
      onStoryId?.(sid, result.answer);
      await loadStory(sid);
    }
    return sid;
  }, [storyId, storybookId, promptId, draftTitle, onStoryId, loadStory]);

  const tickRecordingTimer = useCallback(() => {
    setRecordingTime((prev) => {
      const next = prev + 1;
      if (next >= MAX_RECORDING_SECONDS) {
        recordingTimeRef.current = MAX_RECORDING_SECONDS;
        finalizeRecordingRef.current?.();
        return MAX_RECORDING_SECONDS;
      }
      recordingTimeRef.current = next;
      return next;
    });
  }, []);

  const finalizeRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      setRecordingState('processing');
      if (timerRef.current) clearInterval(timerRef.current);
      shouldUploadOnStopRef.current = true;
      rec.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    }
  }, []);

  useEffect(() => {
    finalizeRecordingRef.current = finalizeRecording;
  }, [finalizeRecording]);

  const uploadAudio = async (sid, audioBlob) => {
    const savedDuration = recordingTimeRef.current || recordingTime;
    const formData = new FormData();
    const ext = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
    formData.append('file', audioBlob, `recording.${ext}`);
    await axios.post(`${API}/stories/${sid}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: savedDuration ? { duration_seconds: savedDuration } : undefined,
    });
    if (savedDuration) {
      await axios.put(`${API}/stories/${sid}/duration?duration_seconds=${savedDuration}`);
    }
  };

  const uploadSegment = async (sid, audioBlob) => {
    const savedDuration = recordingTimeRef.current || recordingTime;
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');
    const { data } = await axios.post(`${API}/stories/${sid}/audio/segments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: savedDuration ? { duration_seconds: savedDuration } : undefined,
    });
    return data.segment;
  };

  const startRecording = async () => {
    try {
      const sid = await ensureStory();
      if (!sid) {
        toast.error('Could not prepare voice recording.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMicPermission('ready');

      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      }

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        if (!shouldUploadOnStopRef.current) return;
        shouldUploadOnStopRef.current = false;
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        setIsUploading(true);
        try {
          const hasExisting =
            continueRecording ||
            storyData?.audio_file_url ||
            (storyData?.audio_segments || []).length > 0;
          if (hasExisting) {
            await uploadSegment(sid, blob);
          } else {
            await uploadAudio(sid, blob);
          }
          toast.success('Recording saved.');
          await loadStory(sid);
          setContinueRecording(true);
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Could not save recording.');
        } finally {
          setIsUploading(false);
          recordingTimeRef.current = 0;
          setRecordingState('inactive');
          setRecordingTime(0);
        }
      };

      mediaRecorder.start(1000);
      recordingTimeRef.current = 0;
      setRecordingState('recording');
      setRecordingTime(0);
      timerRef.current = setInterval(tickRecordingTimer, 1000);
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setMicPermission('denied');
        toast.error('Microphone access is blocked.');
      } else {
        toast.error('Could not access microphone.');
      }
    }
  };

  const pauseRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec?.state === 'recording') {
      rec.pause();
      clearInterval(timerRef.current);
      setRecordingState('paused');
    }
  };

  const resumeRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec?.state === 'paused') {
      rec.resume();
      timerRef.current = setInterval(tickRecordingTimer, 1000);
      setRecordingState('recording');
    }
  };

  const stopRecording = () => finalizeRecording();

  const cancelRecording = () => {
    shouldUploadOnStopRef.current = false;
    clearInterval(timerRef.current);
    mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    recordingTimeRef.current = 0;
    setRecordingState('inactive');
    setRecordingTime(0);
  };

  const transcribe = async () => {
    const sid = storyId || (await ensureStory());
    if (!sid) return;
    setIsTranscribing(true);
    setTranscriptionPhase('starting');
    try {
      const fresh = await loadStory(sid);
      const segments = fresh?.audio_segments || [];
      let text = '';
      if (segments.length > 0) {
        const last = segments[segments.length - 1];
        text = await transcribeStorySegmentAndWait(API, sid, last.id, {
          onPhase: setTranscriptionPhase,
        });
      } else if (fresh?.audio_file_url) {
        text = await transcribeStoryAndWait(API, sid, { onPhase: setTranscriptionPhase });
      } else {
        toast.error('Record a voice answer first.');
        return;
      }
      if (text?.trim()) {
        onTranscript?.(text.trim());
        toast.success('Transcription added to your answer.');
      }
      await loadStory(sid);
    } catch (err) {
      toast.error(err?.message || 'Transcription failed.');
      setTranscriptionPhase('failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  const segments = storyData?.audio_segments || [];
  const recordingCount =
    segments.length + (storyData?.audio_file_url && segments.length === 0 ? 1 : 0);
  const audioUrl = storyData?.audio_file_url
    ? resolveAssetUrl(BACKEND, storyData.audio_file_url)
    : segments.length
      ? resolveAssetUrl(BACKEND, segments[segments.length - 1]?.audio_file_url)
      : null;

  const transcriptStatus =
    transcriptionPhase ||
    storyData?.transcription_status ||
    segments[segments.length - 1]?.transcription_status;

  return {
    storyId,
    ensureStory,
    recordingState,
    recordingTime,
    formatTime,
    continueRecording,
    isUploading,
    isTranscribing,
    transcriptionStatusLabel: transcriptionStatusLabel(transcriptStatus),
    transcriptionPhase: transcriptStatus,
    micPermission,
    recordingCount,
    audioUrl,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    transcribe,
    setContinueRecording,
  };
}
